import logger from './logger';
import { getApiUrl, TIMEOUTS } from './config';

// Use a getter so the URL is re-evaluated on each request (allows runtime override via localStorage)
const apiUrl = () => getApiUrl();

export interface AuthUser {
  id: string;
  email: string;
  full_name: string | null;
}

export interface AuthSession {
  user: AuthUser;
  access_token: string;
  expires_at: number;
}

export interface UserProfile {
  id: string;
  full_name: string;
  employee_id: string | null;
  department: string | null;
  phone: string | null;
}

export interface UserRole {
  role: string;
}

class AuthService {
  private readonly SESSION_KEY = 'athenea_session';
  private readonly TOKEN_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

  // Get current session from localStorage
  getSession(): AuthSession | null {
    const sessionData = localStorage.getItem(this.SESSION_KEY);
    if (!sessionData) return null;

    try {
      const session: AuthSession = JSON.parse(sessionData);

      // Check if session is expired
      if (session.expires_at < Date.now()) {
        this.clearSession();
        return null;
      }

      return session;
    } catch {
      this.clearSession();
      return null;
    }
  }

  // Save session to localStorage
  private setSession(user: AuthUser): AuthSession {
    const session: AuthSession = {
      user,
      access_token: this.generateToken(),
      expires_at: Date.now() + this.TOKEN_EXPIRY,
    };

    localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
    return session;
  }

  // Clear session
  clearSession(): void {
    localStorage.removeItem(this.SESSION_KEY);
  }

  // Generate a simple token (in production, this should come from backend)
  private generateToken(): string {
    // Generar token criptográficamente seguro (256 bits)
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, "0")).join("");
  }

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<{ session: AuthSession | null; error: any }> {
    // Intentar primero con el backend real
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUTS.loginRequest);
      
      // Query users table using fetch directly
      const userResponse = await fetch(
        `${apiUrl()}/users?email=eq.${encodeURIComponent(email)}&select=id,email,full_name`,
        {
          headers: {
            'Accept': 'application/json',
          },
          signal: controller.signal,
        }
      );
      
      clearTimeout(timeoutId);

      if (!userResponse.ok) {
        throw new Error('Error al buscar usuario');
      }
      
      const users = await userResponse.json();

      if (!users || users.length === 0) {
        return {
          session: null,
          error: { message: 'Usuario o contraseña incorrectos' },
        };
      }

      const userData = users[0];

      // Verify password using RPC
      const verifyResponse = await fetch(`${apiUrl()}/rpc/verify_password`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userData.id,
          password: password,
        }),
      });

      if (!verifyResponse.ok) {
        throw new Error('Error al verificar contraseña');
      }

      const passwordValid = await verifyResponse.json();

      if (!passwordValid) {
        return {
          session: null,
          error: { message: 'Usuario o contraseña incorrectos' },
        };
      }

      // Create session
      const user: AuthUser = {
        id: userData.id,
        email: userData.email,
        full_name: userData.full_name,
      };

      const session = this.setSession(user);

      return { session, error: null };
    } catch (error: any) {
      logger.error('Sign in error:', error);
      
      return {
        session: null,
        error: { message: error.message || 'Error al iniciar sesión' },
      };
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    this.clearSession();
  }

  // Get user profile
  async getProfile(userId: string): Promise<{ data: UserProfile | null; error: any }> {
    try {
      const response = await fetch(
        `${apiUrl()}/profiles?id=eq.${userId}&select=*`,
        {
          headers: {
            'Accept': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Error al obtener perfil');

      const profiles = await response.json();
      const data = profiles && profiles.length > 0 ? profiles[0] : null;

      return { data, error: null };
    } catch (error: any) {
      logger.error('Get profile error:', error);
      return { data: null, error };
    }
  }

  // Get user role
  async getUserRole(userId: string): Promise<{ data: string | null; error: any }> {
    try {
      const response = await fetch(
        `${apiUrl()}/user_roles?user_id=eq.${userId}&select=role`,
        {
          headers: {
            'Accept': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Error al obtener rol');

      const roles = await response.json();
      const role = roles && roles.length > 0 ? roles[0].role : 'operator';

      return { data: role, error: null };
    } catch (error: any) {
      logger.error('Get user role error:', error);
      return { data: 'operator', error };
    }
  }

  // Listen for session changes (placeholder for compatibility)
  onAuthStateChange(callback: (session: AuthSession | null) => void): () => void {
    // Check session immediately
    callback(this.getSession());

    // Return unsubscribe function
    return () => {};
  }
}

export const authService = new AuthService();
